import React, {
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useState,
} from "react";
import "./TextScramble.css";

const ScrambleContext = createContext(false);
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!<>_{}[]/\\+=*";
const DURATION = 700;
const FRAME_INTERVAL = 55;
const LOOP_PAUSE = 4000;

// Keep the existing section/button element and refs, with one burst per entry.
export const ScrambleScope = forwardRef(function ScrambleScope(
  { component: Component = "section", children, ...props },
  ref
) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  return (
    <Component
      {...props}
      ref={ref}
      onPointerEnter={(event) => {
        if (
          event.pointerType !== "touch" &&
          window.matchMedia("(hover: hover)").matches
        ) {
          setHovered(true);
        }
      }}
      onPointerLeave={() => setHovered(false)}
      onPointerCancel={() => setHovered(false)}
      onFocus={(event) => {
        if (event.target.matches(":focus-visible")) setFocused(true);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <ScrambleContext.Provider value={hovered || focused}>
        {children}
      </ScrambleContext.Provider>
    </Component>
  );
});

export default function TextScramble({ children, loop = false }) {
  const scopeActive = useContext(ScrambleContext);
  const active = loop || scopeActive;
  const text = React.Children.toArray(children).join("");
  const [frame, setFrame] = useState(null);

  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let requestId;
    let repeatTimer;
    let startedAt;
    let lastFrameAt = -FRAME_INTERVAL;

    const reset = () => {
      cancelAnimationFrame(requestId);
      clearTimeout(repeatTimer);
      setFrame(null);
    };

    const animate = (now) => {
      if (startedAt === undefined) startedAt = now;
      const elapsed = now - startedAt;

      if (elapsed >= DURATION) {
        setFrame(null);
        if (loop) repeatTimer = setTimeout(start, LOOP_PAUSE);
        return;
      }

      if (elapsed - lastFrameAt >= FRAME_INTERVAL) {
        const resolved = Math.max(0, (elapsed - 100) / (DURATION - 100));
        setFrame(
          Array.from(text, (character, index) =>
            /\s/.test(character) || index < resolved * text.length
              ? null
              : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
          )
        );
        lastFrameAt = elapsed;
      }
      requestId = requestAnimationFrame(animate);
    };

    const start = () => {
      if (!active || motion.matches) return;
      startedAt = undefined;
      lastFrameAt = -FRAME_INTERVAL;
      requestId = requestAnimationFrame(animate);
    };

    const handleMotionChange = () => {
      reset();
      if (loop) start();
    };

    setFrame(null);
    start();
    // If the preference changes mid-burst, immediately restore the original.
    motion.addEventListener("change", handleMotionChange);
    return () => {
      cancelAnimationFrame(requestId);
      clearTimeout(repeatTimer);
      motion.removeEventListener("change", handleMotionChange);
    };
  }, [active, text, loop]);

  let characterIndex = 0;
  return (
    <span className="text-scramble">
      <span className="text-scramble__accessible">{text}</span>
      <span aria-hidden="true">
        {text.split(/(\s+)/).map((word, wordIndex) => {
          if (/^\s+$/.test(word)) {
            characterIndex += word.length;
            return word;
          }
          return (
            <span className="text-scramble__word" key={wordIndex}>
              {Array.from(word, (character) => {
                const index = characterIndex++;
                const glyph = active && frame ? frame[index] : null;
                return (
                  <span
                    className="text-scramble__character"
                    data-scrambled={glyph ? "true" : undefined}
                    key={index}
                  >
                    <span className="text-scramble__original">{character}</span>
                    {glyph && (
                      <span
                        className={`text-scramble__glyph text-scramble__glyph--${index % 4}`}
                      >
                        {glyph}
                      </span>
                    )}
                  </span>
                );
              })}
            </span>
          );
        })}
      </span>
    </span>
  );
}
